import api from './api';
import { AxiosError } from 'axios';

export type CampaignDetails = {
  dealerName: string;
  campaignName: string;
  budget: number;
  cpm: number;
  floorFrequency: number;
  startDate: string;
  endDate: string;
  contentFocus: string[];
  dayParting: string;
  notes: string;
  impressions?: number;
  audienceReach?: number;
};

export type AudienceTargeting = {
  zipCode: string;
  radius: number;
  zipCodes: string[];
  selectedSegments: string[];
  segmentLabels?: string[];
  audienceMetrics?: {
    totalCount: number;
    emailCount: number;
    phoneCount: number;
    deviceCount: number;
    iPCount: number;
  };
};

export type CreativeFiles = {
  files: Array<{
    name: string;
    url: string;
  }>;
};

export type ContactInfo = {
  agencyName: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
};

export type Campaign = {
  _id: string;
  status: 'pending' | 'active' | 'completed';
  submittedAt: string;
  campaignDetails: CampaignDetails;
  audienceTargeting: AudienceTargeting;
  creativeFiles: {
    files: Array<{
      name: string;
      url: string;
    }>;
  };
  contactInfo: ContactInfo;
};

// POST /api/campaigns
// Request: CampaignData
// Response: { success: boolean, message: string, campaignId: string }
export const submitCampaign = async (data: {
  campaignDetails: CampaignDetails;
  audienceTargeting: AudienceTargeting;
  creativeFiles: CreativeFiles;
  contactInfo: ContactInfo;
}) => {
  try {
    console.log('Submitting campaign to API...');
    const response = await api.post('/api/campaigns', {
      ...data,
      draftId: localStorage.getItem('campaign_draft_id') // Include draft ID if exists
    });
    console.log('API Response Data:', response.data);
    
    // Clear draft after successful submission
    localStorage.removeItem('campaign_draft');
    localStorage.removeItem('campaign_draft_id');
    
    return response.data;
  } catch (error) {
    console.error('Campaign submission error:', error);
    // Save as draft if submission fails
    localStorage.setItem('campaign_draft', JSON.stringify({
      ...data,
      lastAttempt: new Date().toISOString()
    }));
    throw error;
  }
};

// POST /api/campaigns/draft
// Save campaign draft to server
export const saveCampaignDraft = async (data: {
  campaignDetails?: CampaignDetails | null;
  audienceTargeting?: AudienceTargeting | null;
  creativeFiles?: CreativeFiles | null;
  contactInfo?: ContactInfo | null;
}) => {
  try {
    const draftId = localStorage.getItem('campaign_draft_id');
    const endpoint = draftId ? `/api/campaigns/draft/${draftId}` : '/api/campaigns/draft';
    const method = draftId ? 'put' : 'post';
    
    const response = await api[method](endpoint, data);
    
    // Store draft ID for future updates
    if (response.data.draftId) {
      localStorage.setItem('campaign_draft_id', response.data.draftId);
    }
    
    return response.data;
  } catch (error) {
    console.error('Draft save error:', error);
    // Store locally if server save fails
    localStorage.setItem('campaign_draft', JSON.stringify({
      ...data,
      lastSaved: new Date().toISOString()
    }));
    throw error;
  }
};

// GET /api/campaigns/draft/:id
// Load campaign draft from server
export const loadCampaignDraft = async (draftId: string) => {
  try {
    const response = await api.get(`/api/campaigns/draft/${draftId}`);
    return response.data;
  } catch (error) {
    console.error('Draft load error:', error);
    // Try to load from local storage if server load fails
    const localDraft = localStorage.getItem('campaign_draft');
    if (localDraft) {
      return JSON.parse(localDraft);
    }
    throw error;
  }
};

// GET /api/campaigns/zipCodes
// Request: { zipCode: string, radius: number }
// Response: { zipCodes: string[] }
export const fetchZipCodes = (/* zipCode: string, radius: number */) => {
  // Mocking the response
  return new Promise<{ zipCodes: string[] }>((resolve) => {
    setTimeout(() => {
      resolve({
        zipCodes: ['12345', '12346', '12347', '12348', '12349']
      });
    }, 500);
  });
};

// GET /api/campaigns
// Response: { campaigns: Campaign[] }
export const getCampaigns = async () => {
  try {
    console.log('Fetching campaigns from API...');
    const response = await api.get('/api/campaigns');
    console.log('API Response Data:', response.data);
    
    // Ensure campaigns exist and sort them by submittedAt in descending order
    const campaigns = response.data?.campaigns || [];
    const sortedCampaigns = [...campaigns].sort((a: Campaign, b: Campaign) => 
      new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()
    );
    
    return { campaigns: sortedCampaigns };
  } catch (error) {
    console.error('API error:', error);
    if (error instanceof AxiosError && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

// GET /api/campaigns/:id
// Response: Campaign
export const getCampaignById = async (id: string) => {
  try {
    console.log('Fetching campaign by ID:', id);
    const response = await api.get(`/api/campaigns/${id}`);
    console.log('API Response Data:', response.data);
    
    if (!response.data || !response.data.campaignDetails) {
      throw new Error('Invalid campaign data received');
    }
    
    return response.data;
  } catch (error) {
    console.error('API error:', error);
    if (error instanceof AxiosError) {
      if (error.response?.status === 404) {
        throw new Error('Campaign not found');
      }
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
    }
    throw error;
  }
};